/**
 *  This file contains the common slice of the Redux store.
 * The common slice contains the common states that are used across the application.
 * This is a part of the Redux store in your application. It uses Redux Toolkit's `createSlice` function to generate a slice of the Redux store. A slice includes a reducer, actions, and action creators. 
 *
 * The purpose of this file is to manage a portion of the application's state, specifically the "common" state that is likely shared and used by multiple components or parts of the application. This state includes user information, login status, alert messages, session information, and more. The file also provides action creators, which are functions that components can use to dispatch actions to update this state.
 *
 * This slice contains the following reducers:
 * 1. updateLogin: Updates the isLoggedIn state to true.
 * 2. updateLogout: Updates the isLoggedIn state to false.
 * 3. fullPageLoader: Updates the fullPageLoader state.
 * 4. updateAlertMessage: Updates the alertMessage state.
 * 5. updateIsSessionExpired: Updates the isSessionExpired state.
 * 6. updateIsRefreshTokenExpired: Updates the isRefreshTokenExpired state.
 * 7. updateReloadPageAfterSessionExpired: Updates the reloadPageAfterSessionExpired state.
 * 8. updateUser: Updates the user state.
 * 9. updateCurrentPrompt: Updates the currentPrompt state.
 * 10. updateTileInfo: Updates the TileInfo state.
 * 11. updatePageTranslation: Updates the PageTranslation state.
 * 
 */


import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { commonSliceInterface } from '../interfaces/common/common.interface'
import type { RootState } from './index'

    const initialState: commonSliceInterface = {
    isAuthChecking: true,
    isLoggedIn: false,
    fullPageLoader: false,
    alertMessage: { status: '', message: '', timeout: null, isManualDismiss: false, isNetworkError: false },
    isSessionExpired: false,
    isRefreshTokenExpired: false,
    reloadPageAfterSessionExpired: true,
    TranslationEditFlag: false,
    user: {
        userEmail: '',
        userBusinessEmail: '',
        userId: 0,
        firstName: '',
        lastName: '',
        picPath: '',
        totalCCPoints: 0,
        userCCBalance: 0,
        orgCCBalance: 0,
        usrCreatedDate: '',
        referralId: '',
        blcFlag: false,
        accId: 0,
        orgId: 0,
        accountType: '',
        paginationRecordsPerPage: 10,
        promptExeAlert: 1,
    },
    currentPrompt: {},
    TileInfo: [],
    PageTranslation: [],
    PageMemory: [],
    LibraryType: 'personal',
    blcUserGroup: {
        blcAdmin: null,
        blcSystemManager: null,
        blcPortalManager: null
    }
    }

export const commonSlice = createSlice({
    name: 'commonSlice',
    initialState,
    reducers: {
        updateLogin: (state) => {
            state.isLoggedIn = true;
        },
        setAuthChecking: (state, action: PayloadAction<boolean>) => {
            state.isAuthChecking = action.payload;
        },
        updateLogout: (state) => {
            state.isLoggedIn = false;
            state.user = {
                userEmail: '',
                userBusinessEmail: '',
                userId: 0,
                firstName: '',
                lastName: '',
                picPath: '',
                totalCCPoints: 0,
                userCCBalance: 0,
                orgCCBalance: 0,
                usrCreatedDate: '',
                referralId: '',
                blcFlag: false,
                accId: 0,
                orgId: 0,
                accountType: '',
                paginationRecordsPerPage: 10,
                promptExeAlert: 1,
            };
            state.blcUserGroup = {
                blcAdmin: null,
                blcSystemManager: null,
                blcPortalManager: null
            };
            state.LibraryType = 'personal';
            state.TranslationEditFlag = false;
            state.currentPrompt = {};
            state.TileInfo = [];
            state.PageTranslation = [];
            state.PageMemory = [];
            state.isSessionExpired = false;
            state.isRefreshTokenExpired = false;
            state.reloadPageAfterSessionExpired = true;
            state.fullPageLoader = false;
        },
        fullPageLoader: (state, action: PayloadAction<boolean>) => {
            state.fullPageLoader = action.payload;
        },
        updateAlertMessage: (state, action: PayloadAction<commonSliceInterface['alertMessage']>) => {
            if (!action.payload) {
                state.alertMessage = action.payload;
                return;
            }
            // 1. Check if the screen currently has a high-priority network/500 error showing
            const isCurrentlyNetworkError = state.alertMessage?.isNetworkError;
            // 2. Check if the component is just trying to clear the toast (we should allow this)
            const incomingIsClearingToast = action.payload.status === '';
            // 3. Check if the incoming error is also a high-priority network error (allow overwriting a network error with another network error)
            const incomingIsNetworkError = action.payload.isNetworkError;

            // Priority Lock: If a network error is active, ignore generic local component errors
            if (isCurrentlyNetworkError && !incomingIsClearingToast && !incomingIsNetworkError) {
                return; 
            }
            
            state.alertMessage = {
                status: action.payload.status,
                message: action.payload.message,
                timeout: action.payload.timeout ?? null,
                isManualDismiss: action.payload.isManualDismiss ?? false,
                isNetworkError: action.payload.isNetworkError ?? false,
            };
        },
        updateIsSessionExpired: (state, action: PayloadAction<boolean>) => {
            state.isSessionExpired = action.payload;
        },
        updateIsRefreshTokenExpired: (state, action: PayloadAction<boolean>) => {
            state.isRefreshTokenExpired = action.payload;
        },
        updateReloadPageAfterSessionExpired: (state, action: PayloadAction<boolean>) => {
            state.reloadPageAfterSessionExpired = action.payload;
        },
        updateUser: (state, action: PayloadAction<commonSliceInterface['user']>) => {
            state.user = action.payload;
        },
        updateCurrentPrompt: (state, action: PayloadAction<object>) => {
            state.currentPrompt = action.payload;
        },
        updateTileInfo: (state, action: PayloadAction<commonSliceInterface['TileInfo']>) => {
            state.TileInfo = action.payload;
        },
        updatePageTranslation: (state, action: PayloadAction<commonSliceInterface['PageTranslation']>) => {
            state.PageTranslation = action.payload;
        },
        updatePageMemory: (state, action: PayloadAction<commonSliceInterface['PageMemory']>) => {
            state.PageMemory = action.payload;
        },
        updateLibraryType: (state, action: PayloadAction<string>) => {
            state.LibraryType = action.payload;
        },
        updateTranslationEditFlag: (state, action: PayloadAction<boolean>) => {
            state.TranslationEditFlag = action.payload;
        },
        updateBLCUserGroup: (state, action: PayloadAction<commonSliceInterface['blcUserGroup']>) => {
            state.blcUserGroup = action.payload;
        }
    },
})

// Action creators are generated for each case reducer function
export const { 
    updateLogin, 
    updateLogout, 
    fullPageLoader, 
    updateAlertMessage, 
    updateIsSessionExpired, 
    updateIsRefreshTokenExpired, 
    updateReloadPageAfterSessionExpired, 
    updateUser, updateCurrentPrompt, 
    updateTileInfo,
    updatePageTranslation,
    updatePageMemory,
    updateLibraryType,
    updateTranslationEditFlag,
    updateBLCUserGroup,
    setAuthChecking
} = commonSlice.actions

export const selectCommonSlice = (state: RootState) => state.commonSlice;
export const selectUser = (state: RootState) => state.commonSlice.user;
export const selectIsLoggedIn = (state: RootState) => state.commonSlice.isLoggedIn;
export const selectTranslationEditFlag = (state: RootState) => state.commonSlice.TranslationEditFlag;
export const selectFullPageLoader = (state: RootState) => state.commonSlice.fullPageLoader;
export const selectAlertMessage = (state: RootState) => state.commonSlice.alertMessage;
export const selectCurrentPrompt = (state: RootState) => state.commonSlice.currentPrompt;
export const selectTileInfo = (state: RootState) => state.commonSlice.TileInfo;
export const selectPageTranslation = (state: RootState) => state.commonSlice.PageTranslation;
export const selectPageMemory = (state: RootState) => state.commonSlice.PageMemory;
export const selectLibraryType = (state: RootState) => state.commonSlice.LibraryType;
export const selectBlcUserGroup = (state: RootState) => state.commonSlice.blcUserGroup;
export const selectIsSessionExpired = (state: RootState) => state.commonSlice.isSessionExpired;
export const selectIsRefreshTokenExpired = (state: RootState) => state.commonSlice.isRefreshTokenExpired;
export const selectReloadPageAfterSessionExpired = (state: RootState) => state.commonSlice.reloadPageAfterSessionExpired;
export const selectIsAuthChecking = (state: RootState) => state.commonSlice.isAuthChecking;

export const selectAuthSession = createSelector(
    [
        selectBlcUserGroup,
        selectIsSessionExpired,
        selectReloadPageAfterSessionExpired,
        selectIsRefreshTokenExpired,
        selectIsAuthChecking,
        selectIsLoggedIn,
    ],
    (
        blcUserGroup,
        isSessionExpired,
        reloadPageAfterSessionExpired,
        isRefreshTokenExpired,
        isAuthChecking,
        isLoggedIn
    ) => ({
        blcUserGroup,
        isSessionExpired,
        reloadPageAfterSessionExpired,
        isRefreshTokenExpired,
        isAuthChecking,
        isLoggedIn,
    })
);

export default commonSlice.reducer
